import { useCallback, useEffect, useState } from 'react';
import type { AlertRule } from './useAlerts';

/**
 * Web Push subscription management. Registers the service worker, requests
 * notification permission, subscribes via PushManager, and syncs the
 * subscription + the user's enabled alert rules to the server so the scheduled
 * collector can deliver alerts 24/7.
 */

// VAPID public key. A public key is safe to ship in client code (it's sent to
// push services in plaintext by design) — so we hardcode it as the default and
// allow a build-time env override. It MUST equal the collect-scheduled
// function's VAPID_PUBLIC_KEY (set on Netlify). The matching PRIVATE key stays
// server-only. Regenerate a pair with `npx web-push generate-vapid-keys`.
const DEFAULT_VAPID_PUBLIC_KEY =
  'BBinzsdrOYBg4r5TrVUCcrq3mazb7LjP5nn-IyyKKhH7HDGVuWiyW2_mOi4q7blWjo5gRVNMbxUhDe5ht6W0Wm4';

const VAPID_PUBLIC_KEY =
  (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ||
  DEFAULT_VAPID_PUBLIC_KEY;

export type PushStatus =
  | 'unsupported' // browser can't do web push
  | 'ios-needs-install' // iOS Safari, not installed as PWA
  | 'unconfigured' // no VAPID key configured
  | 'default' // supported, not yet subscribed
  | 'denied' // permission denied
  | 'subscribed'; // active subscription

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function isIos(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac; detect touch.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari-specific flag.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function detectBaseStatus(): PushStatus {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    // iOS only supports push when installed to the home screen.
    if (typeof navigator !== 'undefined' && isIos() && !isStandalone()) {
      return 'ios-needs-install';
    }
    return 'unsupported';
  }
  if (isIos() && !isStandalone()) return 'ios-needs-install';
  if (!VAPID_PUBLIC_KEY) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  return 'default';
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

export function usePushNotifications() {
  const [status, setStatus] = useState<PushStatus>('default');
  const [busy, setBusy] = useState(false);

  // Establish the current state on mount.
  useEffect(() => {
    const base = detectBaseStatus();
    if (base !== 'default') {
      setStatus(base);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancelled) setStatus(sub ? 'subscribed' : 'default');
      } catch {
        if (!cancelled) setStatus('default');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Subscribe this device and register it with the server. */
  const subscribe = useCallback(async (rules: AlertRule[]): Promise<boolean> => {
    if (!VAPID_PUBLIC_KEY) {
      setStatus('unconfigured');
      return false;
    }
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'default');
        return false;
      }
      const reg = await getRegistration();
      await navigator.serviceWorker.ready;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      const res = await fetch('/.netlify/functions/push-subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON(), rules }),
      });
      if (!res.ok) throw new Error(`register failed: ${res.status}`);

      setStatus('subscribed');
      return true;
    } catch {
      setStatus(Notification.permission === 'denied' ? 'denied' : 'default');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  /** Unsubscribe this device and remove it from the server. */
  const unsubscribe = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe().catch(() => {});
        await fetch('/.netlify/functions/push-subscribe', {
          method: 'DELETE',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ endpoint }),
        }).catch(() => {});
      }
      setStatus('default');
    } finally {
      setBusy(false);
    }
  }, []);

  /** Push updated rules to the server without re-prompting (if subscribed). */
  const syncRules = useCallback(
    async (rules: AlertRule[]): Promise<void> => {
      if (status !== 'subscribed') return;
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!sub) return;
        await fetch('/.netlify/functions/push-subscribe', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ subscription: sub.toJSON(), rules }),
        });
      } catch {
        /* best-effort */
      }
    },
    [status],
  );

  /** Send an immediate test notification to this device. */
  const sendTest = useCallback(async (): Promise<boolean> => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (!sub) return false;
      const res = await fetch('/.netlify/functions/push-test', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  return { status, busy, subscribe, unsubscribe, syncRules, sendTest };
}
